import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * useLiveRoomWS — the ONE authenticated WebSocket per live room.
 *
 * Owned by LiveRoom (one connection per room); every overlay subscribes
 * through <LiveRoomSocketContext> — no second sockets, no REST polling.
 *
 *  - `hello` / `sync` frames carry the authoritative room snapshot
 *  - 20 s heartbeat ping → `pong` (also carries presence + host heartbeat);
 *    a missed pong (10 s) force-closes so the reconnect path takes over
 *  - exponential backoff reconnect (1 s → 30 s); one `sync` after reopen
 *  - duplicate-event protection via server-stamped event ids
 */
const PING_MS = 20_000;
const PONG_TIMEOUT_MS = 10_000;
const SEEN_MAX = 600;

export const LiveRoomSocketContext = createContext(null);

const NOOP_SOCKET = { connected: false, snapshot: null, subscribe: () => () => {}, requestSync: () => {} };
export function useLiveRoomSocket() {
  return useContext(LiveRoomSocketContext) || NOOP_SOCKET;
}

/** Subscribe to every frame on the shared room socket (no extra connection). */
export function useLiveRoomEvents(onEvent, { enabled = true } = {}) {
  const socket = useLiveRoomSocket();
  const ref = useRef(onEvent);
  useEffect(() => { ref.current = onEvent; });
  useEffect(() => {
    if (!enabled) return undefined;
    return socket.subscribe("*", (msg) => ref.current?.(msg));
  }, [socket, enabled]);
}

export default function useLiveRoomWS(streamId, { enabled = true, onEvent, onChat, onGift, onParticipantLeave, token } = {}) {
  const [stage, setStage] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [ended, setEnded] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [reconnects, setReconnects] = useState(0);

  const onEventRef = useRef(onEvent);
  const onChatRef = useRef(onChat);
  const onGiftRef = useRef(onGift);
  const onParticipantLeaveRef = useRef(onParticipantLeave);
  const tokenRef = useRef(token);
  useEffect(() => { onEventRef.current = onEvent; });
  useEffect(() => { onChatRef.current = onChat; });
  useEffect(() => { onGiftRef.current = onGift; });
  useEffect(() => { onParticipantLeaveRef.current = onParticipantLeave; });
  useEffect(() => { tokenRef.current = token; });

  const listenersRef = useRef(new Map());
  const sendRef = useRef(() => {});
  const snapshotRef = useRef(null);

  const emit = (channel, msg) => {
    const run = (set) => set && set.forEach((fn) => { try { fn(msg); } catch { /* listener error */ } });
    run(listenersRef.current.get(channel));
    run(listenersRef.current.get("*"));
  };

  const socket = useMemo(() => ({
    subscribe: (channel, fn) => {
      const set = listenersRef.current.get(channel) || new Set();
      set.add(fn);
      listenersRef.current.set(channel, set);
      // Late subscribers get the current snapshot immediately.
      if (channel === "sync" && snapshotRef.current) { try { fn(snapshotRef.current); } catch { /* noop */ } }
      return () => { set.delete(fn); };
    },
    requestSync: () => sendRef.current({ type: "sync" }),
  }), []);

  useEffect(() => {
    if (!enabled || !streamId) return undefined;
    let ws;
    let cancelled = false;
    let retry = 0;
    let retryTimer = null;
    let halted = false;
    let handshakeFails = 0;
    let pingTimer = null;
    let pongTimer = null;
    let everOpened = false;
    const MAX_HANDSHAKE_FAILS = 6;
    const seen = new Set();
    const seenQueue = [];

    const send = (obj) => { try { if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj)); } catch { /* closed */ } };
    sendRef.current = send;

    const stopHeartbeat = () => { clearInterval(pingTimer); clearTimeout(pongTimer); pingTimer = null; pongTimer = null; };
    const startHeartbeat = () => {
      stopHeartbeat();
      pingTimer = setInterval(() => {
        send({ type: "ping" });
        clearTimeout(pongTimer);
        pongTimer = setTimeout(() => { try { ws && ws.close(4000, "pong-timeout"); } catch { /* noop */ } }, PONG_TIMEOUT_MS);
      }, PING_MS);
    };

    const applySnapshot = (state) => {
      if (!state) return;
      snapshotRef.current = state;
      setSnapshot(state);
      if (state.stage) setStage(state.stage);
      if (typeof state.viewer_count === "number") setViewerCount(state.viewer_count);
      if (state.is_live === false) { halted = true; setEnded((p) => p || { reason: "host_ended" }); }
      emit("sync", state);
    };

    const connect = () => {
      let opened = false;
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const t = tokenRef.current;
      const suffix = t ? `?token=${encodeURIComponent(t)}` : "";
      const rq = everOpened ? `${suffix ? "&" : "?"}r=${retry + 1}` : "";
      const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/rooms/${streamId}${suffix}${rq}`;
      try { ws = new WebSocket(wsUrl); } catch { scheduleRetry(); return; }
      ws.onopen = () => {
        if (cancelled) return;
        opened = true;
        retry = 0;
        handshakeFails = 0;
        if (everOpened) setReconnects((n) => n + 1);
        everOpened = true;
        setWsConnected(true);
        startHeartbeat();
      };
      ws.onmessage = (e) => {
        if (cancelled) return;
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.id) {
          if (seen.has(msg.id)) return;
          seen.add(msg.id); seenQueue.push(msg.id);
          if (seenQueue.length > SEEN_MAX) seen.delete(seenQueue.shift());
        }
        onEventRef.current?.(msg);
        switch (msg.channel) {
          case "hello":
          case "sync":
            applySnapshot(msg.state);
            break;
          case "pong":
            clearTimeout(pongTimer);
            if (msg.ended) { halted = true; setEnded((p) => p || { reason: "host_ended" }); }
            break;
          case "stage:update":
            if (msg.stage) setStage(msg.stage);
            break;
          case "viewer:count":
            if (typeof msg.count === "number") setViewerCount(msg.count);
            break;
          case "participant:join":
            if (typeof msg.viewer_count === "number") setViewerCount(msg.viewer_count);
            break;
          case "participant:leave":
            if (typeof msg.viewer_count === "number") setViewerCount(msg.viewer_count);
            if (msg.user_id) onParticipantLeaveRef.current?.(msg);
            break;
          case "participants:delta":
            // Coalesced joins/leaves (one frame per 300 ms window per pod).
            if (typeof msg.viewer_count === "number") setViewerCount(msg.viewer_count);
            (msg.left || []).forEach((uid) => onParticipantLeaveRef.current?.({ ...msg, user_id: uid }));
            break;
          case "stream:end":
          case "live:end":
            halted = true;
            setEnded({ reason: msg.reason || "host_ended" });
            break;
          case "chat:new":
            if (msg.message) onChatRef.current?.(msg.message);
            break;
          case "gift:sent":
            if (msg.gift) onGiftRef.current?.(msg.gift, msg);
            break;
          default:
            break;
        }
        if (msg.channel && msg.channel !== "hello" && msg.channel !== "sync") emit(msg.channel, msg);
      };
      ws.onclose = (e) => {
        if (cancelled) return;
        stopHeartbeat();
        setWsConnected(false);
        if (e.code === 4404) { halted = true; setEnded((prev) => prev || { reason: "not_found" }); return; }
        if (e.code === 4401 || e.code === 4403) { halted = true; return; }
        if (!opened) {
          handshakeFails += 1;
          if (handshakeFails >= MAX_HANDSHAKE_FAILS) { halted = true; return; }
        }
        scheduleRetry();
      };
      ws.onerror = () => { /* onclose follows */ };
    };

    const scheduleRetry = () => {
      if (cancelled || halted) return;
      const jitter = Math.random() * 300;
      const delay = Math.min(1000 * 2 ** retry, 30000) + jitter;
      retry += 1;
      retryTimer = setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelled = true;
      stopHeartbeat();
      if (retryTimer) clearTimeout(retryTimer);
      sendRef.current = () => {};
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [enabled, streamId]);

  const value = useMemo(() => ({ ...socket, connected: wsConnected, snapshot, reconnects }), [socket, wsConnected, snapshot, reconnects]);
  return { stage, viewerCount, ended, wsConnected, snapshot, reconnects, socket: value };
}
